import React, { useState, useEffect, use } from "react";
import Form from "../../../../Components/Form";
import TextInput from "../../../../Components/Form/TextInput";
import NumberInput from "../../../../Components/Form/NumberInput";
import RadioButton from "../../../../Components/Form/RadioButton";
import Product from "../../../../APIController/Product";
import Text from "../../../../Components/Text";
import Snackbar from "../../../../Components/Elements/Snackbar";
import ColorPicker from "../../../../Components/Form/ColorPicker";
import Checkbox from "../../../../Components/Form/Checkbox";
import WorkspaceSelector from "../../../../Helper/WorkspaceSelector";

const ProductForm = ({ selectedProduct = null, afterAction, categoryId }) => {
    //API
    const { addProduct, editProduct } = Product()

    const [snackbarMessage, setSnackbarMessage] = useState("");
    const { getFullCurrentWorkspace } = WorkspaceSelector()
    const subscription = getFullCurrentWorkspace()?.workspaceDTO?.subscription;
    const isTester = getFullCurrentWorkspace()?.workspaceDTO?.freeTester;

    // MWST FOR SWITZERLAND (ADD OTHER RATES LATER AND ADD COUNTRY FIELD TO WORKSPACE)
    const VALID_MWST_RATES = [8.1, 2.6, 3.8]

    function isAllowed(target) {
        return isTester || (subscription && subscription[target])
    }

    //ProductDTO attributes
    const [productCategoryId, setProductCategoryId] = useState(null)
    const [productName, setProductName] = useState("")
    const [productPrice, setProductPrice] = useState(null)
    const [productMwst, setProductMwst] = useState(null)
    const [productToGoMwst, setProductToGOMwst] = useState(null)
    const [productBuyingPrice, setProductBuyingPrice] = useState(null)
    const [productReducable, setProductReducable] = useState(false)
    const [productEan, setProductEan] = useState(null)
    const [productColorCode, setProductColorCode] = useState(null)
    const [catIndex, setCatIndex] = useState(null)
    const [stockManaged, setStockManaged] = useState(false)

    /* Edit handler */
    useEffect(() => {
        if (selectedProduct) {
            setProductCategoryId(selectedProduct.categoryId)
            setProductName(selectedProduct.name)
            setProductPrice(selectedProduct.price)
            setProductBuyingPrice(selectedProduct.buyingPrice)
            setProductReducable(selectedProduct.reduceable || false)
            setStockManaged(selectedProduct.stockManaged || false)
            setProductEan(selectedProduct.ean || null)
            setProductColorCode(selectedProduct.colorCode || null)
            setCatIndex(selectedProduct.index)
            setProductMwst(selectedProduct.mwst || null)
            setProductToGOMwst(selectedProduct.toGoMwst || null)
        } else {
            setProductCategoryId(categoryId)
        }
    }, [])

    /* EAN Validation Functions */
    const isValidEAN13 = (ean) => {
        if (!/^\d{13}$/.test(ean)) return false;

        const digits = ean.split('').map(Number);
        const checkDigit = digits.pop();
        const sum = digits.reduce((acc, digit, idx) => {
            return acc + digit * (idx % 2 === 0 ? 1 : 3);
        }, 0);
        const calculatedCheck = (10 - (sum % 10)) % 10;
        return checkDigit === calculatedCheck;
    };

    const isValidEAN8 = (ean) => {
        if (!/^\d{8}$/.test(ean)) return false;

        const digits = ean.split('').map(Number);
        const checkDigit = digits.pop();
        const sum = digits.reduce((acc, digit, idx) => {
            return acc + digit * (idx % 2 === 0 ? 3 : 1);
        }, 0);
        const calculatedCheck = (10 - (sum % 10)) % 10;
        return checkDigit === calculatedCheck;
    };

    /* Submithandler */
    const handleAddProduct = async (productDTO) => {
        await addProduct(productDTO)
        afterAction()
    }

    const handleEditProduct = async (id, productDTO) => {
        await editProduct(id, productDTO)
        afterAction()
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (productMwst != null && !VALID_MWST_RATES.includes(parseFloat(productMwst))) {
            setSnackbarMessage("Ungültiger MWST-Satz. Erlaubt: 8.1%, 2.6% oder 3.8%.")
            return
        }
        if (productToGoMwst != null && productToGoMwst !== "" && !VALID_MWST_RATES.includes(parseFloat(productToGoMwst))) {
            setSnackbarMessage("Ungültiger Take-Away-MWST-Satz. Erlaubt: 8.1%, 2.6% oder 3.8%.")
            return
        }

        const ean = productEan?.toString().trim();

        if (ean) {
            if (/^\d{13}$/.test(ean)) {
                if (!isValidEAN13(ean)) {
                    setSnackbarMessage("EAN-13 Prüfziffer ist ungültig.");
                    return;
                }
            }
            else if (/^\d{8}$/.test(ean)) {
                if (!isValidEAN8(ean)) {
                    setSnackbarMessage("EAN-8 Prüfziffer ist ungültig.");
                    return;
                }
            }
            else {
                setSnackbarMessage("EAN muss aus genau 8 oder 13 Ziffern bestehen.");
                return;
            }
        }

        const productDTO = {
            categoryId: categoryId,
            name: productName,
            price: productPrice,
            buyingPrice: productBuyingPrice,
            reduceable: productReducable,
            ean: productEan,
            colorCode: productColorCode,
            index: catIndex,
            stockManaged: stockManaged,
            mwst: productMwst,
            toGomwst: productToGoMwst
        }

        if (selectedProduct) {
            handleEditProduct(selectedProduct.id, productDTO)
        } else {
            handleAddProduct(productDTO)
        }
    }

    /* Form */
    return (
        <Form onSubmitAction={handleSubmit} submitButtonText={selectedProduct ? "Produkt bearbeiten" : "Produkt erstellen"}>
            <Text type="maintitle" classNames={"flex text-center justify-center"}> {selectedProduct ? "Produkt bearbeiten" : "Produkt erstellen"}</Text>
            <TextInput attribute={productName} id={"productName"} label={"Name"} placeholder={"Produkt Name"} action={(e) => setProductName(e.target.value)} />
            <NumberInput
                attribute={catIndex}
                id={"catIndex"}
                label={"Reihenfolge"}
                action={(e) => setCatIndex(e.target.value)}
            />
            <NumberInput attribute={productPrice} id={"productPrice"} label={"Preis"} placeholder={"Produkt Preis"} action={(e) => setProductPrice(e.target.value)} />
            <NumberInput attribute={productBuyingPrice} id={"productBuyingPrice"} label={"Einkaufspreis"} placeholder={"Produkt Einkaufspreis"} action={(e) => setProductBuyingPrice(e.target.value)} required={false} />
            <NumberInput attribute={productMwst} id={"productMwst"} label={"Mehrwertsteuer in %"} placeholder={"Produkt Mehrwertsteuer"} action={(e) => setProductMwst(e.target.value)} />
            <NumberInput attribute={productToGoMwst} id={"productToGoMwst"} label={"Take Away Mehrwertsteuer in %"} placeholder={"Take Away Produkt Mehrwertsteuer"} action={(e) => setProductToGOMwst(e.target.value)} required={false} />
            <Text>* Nur falls ein andere Satz benötigt wird</Text>
            <NumberInput attribute={productEan} id={"productEan"} label={"EAN"} placeholder={"Produkt EAN Code (8 oder 13 Ziffern)"} action={(e) => setProductEan(e.target.value)} required={false} />
            {isAllowed("useInventoryManagement") && (
                <Checkbox
                    attribute={stockManaged}
                    id="stockManaged"
                    label="Bestand verwalten"
                    required={false}
                    action={(e) => setStockManaged(e.target.checked)}
                />
            )}

            <RadioButton
                attribute1={true} attribute2={false}
                id={"reducable"}
                label1={"Ja"} label2={"Nein"}
                checked1={productReducable === true} checked2={productReducable === false}
                title={"Rabattfähig"}
                action1={() => setProductReducable(true)} action2={() => setProductReducable(false)} />
            <ColorPicker action={(color) => setProductColorCode(color)} label={"Farbe"} initialColor={productColorCode} />
            <Snackbar message={snackbarMessage} onClose={() => setSnackbarMessage("")} />
        </Form>
    )
}
export default ProductForm